import { z } from 'zod';

import { apiRequest } from '@/api/client';
import { tokenStore } from '@/api/tokenStore';
import { billingLockSchema } from '@/features/billing/api/billing';

/** Schemas mirroring `app/schemas/auth.py`. */

export const permissionSchema = z.string();

export const userProfileSchema = z.object({
  id: z.string(),
  /** Null for a WhatsApp sign-up that has not added an address yet. */
  email: z.string().nullable(),
  full_name: z.string(),
  phone: z.string().nullable().optional(),
  role: z.string(),
  status: z.string(),
  tenant_id: z.string().nullable().optional(),
  email_verified_at: z.string().nullable().optional(),
  last_login_at: z.string().nullable().optional(),
  created_at: z.string(),
});

export const sessionSchema = z.object({
  access_token: z.string(),
  token_type: z.string(),
  expires_in: z.number(),
  user: userProfileSchema,
  permissions: z.array(permissionSchema),
});

export const currentUserSchema = z.object({
  user: userProfileSchema,
  permissions: z.array(permissionSchema),
  has_business: z.boolean(),
  /**
   * True when the password this session was opened with was chosen by somebody
   * else - an owner creating the account, or resetting it. The API then refuses
   * everything but changing it, so the client has to send them to the
   * change-password screen rather than render a dashboard that cannot load.
   *
   * Optional so an older backend, which does not send it, reads as false rather
   * than failing the whole `/auth/me` parse and signing everybody out.
   */
  must_change_password: z.boolean().optional().default(false),
  /**
   * Set while this merchant's plan has lapsed past its grace period: the
   * store is closed and the dashboard is limited to billing. Optional and
   * defaulted for the same reason as the flag above - an older backend sends
   * nothing, and nothing means not locked.
   */
  billing_lock: billingLockSchema.nullable().optional().default(null),
});

export const registerResultSchema = z.object({
  user: userProfileSchema,
  requires_email_verification: z.boolean(),
  /**
   * The code's lifetime, and the wait before another can be asked for, both in
   * seconds. Optional with a default so an older backend - which sends
   * neither - reads as zero rather than failing the parse and losing an
   * account that has just been created.
   */
  code_expires_in: z.number().optional().default(0),
  code_resend_after: z.number().optional().default(0),
});

export const emailCodeSchema = z.object({
  message: z.string(),
  expires_in: z.number(),
  resend_after: z.number(),
});

export const messageSchema = z.object({ message: z.string() });

export type UserProfile = z.infer<typeof userProfileSchema>;
export type Session = z.infer<typeof sessionSchema>;
export type CurrentUser = z.infer<typeof currentUserSchema>;
export type RegisterResult = z.infer<typeof registerResultSchema>;
export type EmailCodeSent = z.infer<typeof emailCodeSchema>;

export interface RegisterPayload {
  email: string;
  password: string;
  full_name: string;
  phone?: string;
  /** A partner's referral code, if the merchant arrived holding one. */
  referral_code?: string;
  /**
   * Where the code came from. A code that rode in on the link is attribution
   * the partner earned by getting somebody to click; one typed into the box was
   * passed on by word of mouth. Both count, and the programme wants to know
   * which is working, so the client says rather than the server guessing.
   */
  referral_source?: 'FORM' | 'LINK';
  /**
   * The versions of NeonCarts' policies the person ticked the box for. The
   * server refuses the registration if a policy that requires acceptance is
   * missing, or if one of these is no longer the version in force.
   */
  accepted_policy_version_ids?: string[];
}

export function register(payload: RegisterPayload): Promise<RegisterResult> {
  return apiRequest('/auth/register', registerResultSchema, {
    method: 'POST',
    body: payload,
    skipAuthRefresh: true,
  });
}

/** `captchaToken` once the API has asked for a Turnstile check. */
export async function login(
  email: string,
  password: string,
  captchaToken?: string,
): Promise<Session> {
  const session = await apiRequest('/auth/login', sessionSchema, {
    method: 'POST',
    body: { email, password, captcha_token: captchaToken },
    skipAuthRefresh: true,
  });
  tokenStore.set(session.access_token);
  return session;
}

export async function logout(): Promise<void> {
  try {
    await apiRequest('/auth/logout', messageSchema, {
      method: 'POST',
      skipAuthRefresh: true,
    });
  } finally {
    // Whatever the server said, this browser is signed out.
    tokenStore.clear();
  }
}

export function fetchCurrentUser(): Promise<CurrentUser> {
  return apiRequest('/auth/me', currentUserSchema);
}

/**
 * Confirm an address with the six-digit code registration emailed.
 *
 * The address travels with the code because there is no session yet: an
 * account that has not been verified cannot sign in, so nothing else says
 * which account is being proved.
 */
export function verifyEmailCode(email: string, code: string): Promise<{ message: string }> {
  return apiRequest('/auth/verify-email-code', messageSchema, {
    method: 'POST',
    body: { email, code },
    skipAuthRefresh: true,
  });
}

/** Ask for another code. Answers the same way whatever the address is. */
export function resendVerificationCode(email: string): Promise<EmailCodeSent> {
  return apiRequest('/auth/resend-verification-code', emailCodeSchema, {
    method: 'POST',
    body: { email },
    skipAuthRefresh: true,
  });
}

/** The link counterpart, for an address that was sent one. */
export function verifyEmail(token: string): Promise<{ message: string }> {
  return apiRequest('/auth/verify-email', messageSchema, {
    method: 'POST',
    body: { token },
    skipAuthRefresh: true,
  });
}

export function resendVerification(email: string): Promise<{ message: string }> {
  return apiRequest('/auth/resend-verification', messageSchema, {
    method: 'POST',
    body: { email },
    skipAuthRefresh: true,
  });
}

export function forgotPassword(email: string): Promise<{ message: string }> {
  return apiRequest('/auth/forgot-password', messageSchema, {
    method: 'POST',
    body: { email },
    skipAuthRefresh: true,
  });
}

/**
 * Change your own password, proving you know the current one.
 *
 * Every session is ended by the change, this one included, so the caller has to
 * expect to sign in again afterwards.
 */
export function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ message: string }> {
  return apiRequest('/auth/change-password', messageSchema, {
    method: 'POST',
    body: { current_password: currentPassword, new_password: newPassword },
  });
}

export function resetPassword(token: string, password: string): Promise<{ message: string }> {
  return apiRequest('/auth/reset-password', messageSchema, {
    method: 'POST',
    body: { token, password },
    skipAuthRefresh: true,
  });
}
